import React, { useEffect, useRef, useState } from "react";
import { get, set } from "lodash";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import Alert from "@amzn/meridian/alert";
import Loader from "@amzn/meridian/loader";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import RadioButton from "@amzn/meridian/radio-button";
import Column from "@amzn/meridian/column";
import Toaster from "@amzn/meridian/toaster";
import { formatStatus } from "../../table/utils";
import { useAppSelector } from "../../../app/store/hooks";
import { yearBuiltOptions } from "../../../utils/helperUtils";
import Icon from "@amzn/meridian/icon";
import editTokens from "@amzn/meridian-tokens/base/icon/edit";
import Tooltip from "@amzn/meridian/tooltip";
import chevronUpLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-up-large";
import closeLargeTokens from "@amzn/meridian-tokens/base/icon/close-large";
import saveTokens from "@amzn/meridian-tokens/base/icon/save";
import syncTokens from "@amzn/meridian-tokens/base/icon/sync";
import styles from "../style.module.scss";
import { useNavigate } from "react-router-dom";

export const AppealsTabNavigation = {
    SUMMARY: "SUMMARY",
    COST: "COST",
    INCOME: "INCOME",
    SALES: "SALES",
} as const;

interface IAssessmentAppealsActionBarProps {
    propertyType: keyof typeof AppealsTabNavigation;
    setPropertyType: (value: keyof typeof AppealsTabNavigation) => void;
    year: string;
    setYear: any;
    isEditing: boolean;
    setIsEditing: any;
    updateData: any;
    isError: boolean;
    errorMessage: string;
    handleCreateAppeal: any;
    isAppealStatusLoading: boolean;
    data: any;
    appealUpdateError: boolean;
    appealUpdateErrorMessage: any;
}

export const ActionBar: React.FC<IAssessmentAppealsActionBarProps> = ({
    propertyType,
    setPropertyType,
    year,
    setYear,
    isEditing,
    setIsEditing,
    updateData,
    isError,
    errorMessage,
    handleCreateAppeal,
    isAppealStatusLoading,
    data,
    appealUpdateError,
    appealUpdateErrorMessage,
}) => {
    const { alias } = useAppSelector(state => state.auth.user);
    const status = get(data, "appealDetails.status", "");
    const [newStatus, setNewStatus] = useState(status);
    const navigate = useNavigate();
    const [errorAlert, setErrorAlert] = useState([]);
    const [openChangeStatusModal, setOpenChangeStatusModal] = useState(false);
    const [openRestartAppealModal, setOpenRestartAppealModal] = useState(false);
    const [isAnotherUserEditing, setIsAnotherUserEditing] = useState(false);

    useEffect(() => {
        setIsAnotherUserEditing(
            get(data, "appealDetails.status", "") === "EDITING" &&
                alias !== get(data, "appealDetails.editingUser", "")
        );
    }, [data, alias]);

    useEffect(() => {
        setIsEditing(status === "EDITING" && get(data, "appealDetails.editingUser", "") === alias);
    }, [data, status, alias, setIsEditing]);

    const handleInputChange = async (field: string, value: any, isSave: boolean) => {
        set(data, field, value);
        await updateData(isSave ? data.appealDetails : {});
    };

    const handleUpdateStatus = async (status: string) => {
        await handleInputChange(`appealDetails.status`, status, false);
    };

    const handleUpdateStatusForSave = async (status: string) => {
        const errorMessage = validate();
        if (errorMessage === "") {
            await handleInputChange(`appealDetails.status`, status, true);
        } else {
            setErrorAlert([{ id: errorMessage, timeout: 20000 }]);
        }
    };

    const syncDataInEdit = async () => {
        const errorMessage = validate();
        if (errorMessage === "") {
            await updateData(data.appealDetails, true);
        } else {
            setErrorAlert([{ id: errorMessage, timeout: 20000 }]);
        }
    };

    const validate = () => {
        const errorMessages = [];
        const incomeSpaceData = get(
            data,
            `appealDetails.appealApproachCalculationData.income.spaces`
        );

        Object.values(incomeSpaceData).forEach((value: any) => {
            const validatedResultMessage = validateWeights(
                value.leaseAdjustment.comps,
                value.spaceDetails.spaceName
            );
            if (validatedResultMessage !== "") {
                errorMessages.push(validatedResultMessage);
            }
        });

        const saleSpaceData = get(data, `appealDetails.appealApproachCalculationData.sales`);
        const validatedResultMessage = validateWeights(saleSpaceData.comps);
        if (validatedResultMessage !== "") {
            errorMessages.push(validatedResultMessage);
        }

        return errorMessages.length === 0
            ? ""
            : `The total weight distribution for these (${errorMessages.join(", ")}) tables does not equal 100%. Please adjust the weights to ensure they sum up to 100% before saving.`;
    };

    const validateWeights = (comps: any, spaceName?: string) => {
        const totalWeight = Object.values(comps).reduce(
            (sum: number, value: any) => sum + (value.enable === true ? Number(value.weight) : 0),
            0
        );

        if (totalWeight >= 101 || totalWeight < 100) {
            return `${spaceName ? `Income:Lease Adjustment:${spaceName}` : "Sales"}`;
        }

        return "";
    };

    const renderErrorAlert = () => (
        <Toaster toasts={errorAlert} onCloseToast={() => setErrorAlert([])}>
            {toast => (
                <>
                    <Box height="50px" />
                    <Alert onClose={toast.onClose} type="error">
                        {toast.id}
                    </Alert>
                </>
            )}
        </Toaster>
    );

    const renderYearSelect = () => (
        <Row alignmentVertical="center">
            <Select
                value={year}
                onChange={setYear}
                size="small"
                disabled={
                    data?.appealDetails?.status === "EDITING" &&
                    alias === get(data, "appealDetails.editingUser", "")
                }
            >
                {yearBuiltOptions.map((item, index) => (
                    <SelectOption key={index} value={item.value} label={item.label} />
                ))}
            </Select>
        </Row>
    );

    const renderStatusChangeModal = () => (
        <Modal
            title="Change status"
            open={openChangeStatusModal}
            onClose={() => setOpenChangeStatusModal(false)}
            scrollContainer="viewport"
            closeLabel="Close"
            aria-describedby="modal-description"
        >
            <Text type="b300">Which status do you want to change to?</Text>
            <Box height="15px" />
            {renderStatusOptions()}
            <ModalFooter>
                <Row alignmentHorizontal="end" widths="fit">
                    <Button
                        type="secondary"
                        size="small"
                        onClick={() => setOpenChangeStatusModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                            handleUpdateStatus(newStatus);
                            setOpenChangeStatusModal(false);
                        }}
                    >
                        Confirm
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );

    const renderStatusOptions = () => {
        const options = {
            NEW: ["PREPARING", "NOT_APPEALED"],
            PREPARING: ["UNDER_APPEAL", "NOT_APPEALED"],
            UNDER_APPEAL: ["PREPARING", "NOT_APPEALED", "COMPLETE"],
            NOT_APPEALED: ["PREPARING"],
        };

        return (options[status] || []).map(option => (
            <RadioButton
                key={option}
                checked={newStatus === option}
                onChange={() => setNewStatus(option)}
                name={option}
                value={option}
            >
                {option.charAt(0) + option.slice(1).toLowerCase().replace("_", " ")}
            </RadioButton>
        ));
    };

    const renderRestartAppealModal = () => (
        <Modal
            title="Restart Valuation"
            open={openRestartAppealModal}
            onClose={() => setOpenRestartAppealModal(false)}
            scrollContainer="viewport"
            closeLabel="Close"
            aria-describedby="modal-description"
        >
            <Text type="b300">Are you sure you want to reset the valuation?</Text>
            <Box height="15px" />
            <Text type="h100">
                Note: This action cannot be undone and your current changes will be lost
            </Text>
            <ModalFooter>
                <Row alignmentHorizontal="end" widths="fit">
                    <Button
                        type="secondary"
                        size="small"
                        onClick={() => setOpenRestartAppealModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                            handleCreateAppeal();
                            setOpenRestartAppealModal(false);
                        }}
                    >
                        Confirm
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );

    if (isError) {
        if (errorMessage.includes("No item found for hashKey")) {
            return (
                <Alert type="warning">
                    <Row width="100%" widths={["fill", "fill"]} spacingInset="none">
                        <Column
                            alignmentHorizontal="start"
                            spacing="none"
                            alignmentVertical="center"
                        >
                            <Text type="h200">No appeal found for the year: {year}</Text>
                            <Text type="b300">Create the appeal using Create appeal button</Text>
                        </Column>
                        <Row alignmentHorizontal="end">
                            <Row>
                                {renderYearSelect()}
                                <Box>
                                    {isAppealStatusLoading ? (
                                        <Loader type="circular" />
                                    ) : (
                                        <Button
                                            size="small"
                                            type="tertiary"
                                            onClick={handleCreateAppeal}
                                        >
                                            Create appeal
                                        </Button>
                                    )}
                                </Box>
                            </Row>
                        </Row>
                    </Row>
                </Alert>
            );
        }

        return (
            <Alert type="warning">
                <Text type="h200">Failed to fetch Appeal details.</Text>
                <Text type="b300">Reason: {errorMessage}</Text>
            </Alert>
        );
    }
    const actionButtonBarRef = useRef(null);

    return (
        <div ref={actionButtonBarRef}>
            {data && (
                <>
                    {appealUpdateError && (
                        <Row width="100%">
                            <Alert size="large" type="warning" width="100%">
                                {appealUpdateErrorMessage.data.message}
                            </Alert>
                        </Row>
                    )}
                    {isAnotherUserEditing && (
                        <Row width="100%">
                            <Alert size="large" type="warning" width="100%">
                                {data?.appealDetails?.editingUser} is editing the appeals package.
                                You can continue editing after they complete their work.
                            </Alert>
                        </Row>
                    )}
                    <Box backgroundColor="subdued" width="fill" overflowX="auto">
                        <Row
                            backgroundColor="subdued"
                            spacingInset="400"
                            alignmentVertical="center"
                            alignmentHorizontal="start"
                            width="fit"
                            minWidth="950px"
                        >
                            <Box width="10%">{renderYearSelect()}</Box>
                            <Box>
                                <Box>{formatStatus(status)}</Box>
                            </Box>
                            <Row alignmentVertical="center">
                                <Box height="50px" type="outline" />
                            </Row>
                            <Box width="auto">
                                <Box height="5px" />
                                <ButtonGroup
                                    value={propertyType}
                                    onChange={setPropertyType}
                                    size="small"
                                >
                                    {Object.entries(AppealsTabNavigation).map(([key, value]) => (
                                        <ButtonOption key={key} value={value}>
                                            {key.charAt(0).toUpperCase() +
                                                key.slice(1).toLowerCase() +
                                                (key.toLowerCase() !== "summary"
                                                    ? " approach"
                                                    : "")}
                                        </ButtonOption>
                                    ))}
                                </ButtonGroup>
                            </Box>
                            <Row alignmentVertical="center">
                                <Box height="50px" type="outline" />
                            </Row>
                            <Box>
                                <Box height="5px" />
                                <Row>
                                    <Button
                                        type={"primary"}
                                        onClick={() => {
                                            handleUpdateStatus("EDITING").then(() =>
                                                navigate(`${year}/export`)
                                            );
                                        }}
                                        disabled={status !== "UNDER_APPEAL"}
                                        size={"small"}
                                    >
                                        Export
                                    </Button>
                                    {isEditing ? (
                                        <>
                                            <Button
                                                size="small"
                                                type="tertiary"
                                                onClick={() =>
                                                    handleUpdateStatusForSave("PREPARING")
                                                }
                                            >
                                                Save changes
                                            </Button>
                                            <Button
                                                size="small"
                                                type="tertiary"
                                                onClick={() => handleUpdateStatus("PREPARING")}
                                            >
                                                Cancel changes
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                size="small"
                                                type="tertiary"
                                                onClick={() => handleUpdateStatus("EDITING")}
                                                disabled={status !== "PREPARING"}
                                            >
                                                Edit appeal
                                            </Button>
                                            <Button
                                                disabled={
                                                    status === "COMPLETE" ||
                                                    status === "EDITING" ||
                                                    data.status === "IN_PROGRESS" ||
                                                    data.status === "FAILED"
                                                }
                                                size="small"
                                                type="tertiary"
                                                onClick={() => setOpenChangeStatusModal(true)}
                                            >
                                                Change status
                                            </Button>
                                            {isAppealStatusLoading ? (
                                                <Loader type="circular" />
                                            ) : (
                                                <Button
                                                    disabled={
                                                        status === "COMPLETE" ||
                                                        status === "NOT_APPEALED" ||
                                                        status === "EDITING" ||
                                                        data.status === "IN_PROGRESS"
                                                    }
                                                    size="small"
                                                    type="tertiary"
                                                    onClick={() => setOpenRestartAppealModal(true)}
                                                >
                                                    Reset Valuation
                                                </Button>
                                            )}
                                            {renderStatusChangeModal()}
                                            {renderRestartAppealModal()}
                                        </>
                                    )}
                                </Row>
                            </Box>
                        </Row>
                        <div className={styles.outerActionButtons}>
                            <Row spacing={"200"}>
                                {isEditing ? (
                                    <React.Fragment>
                                        <Tooltip
                                            position="top"
                                            title="Save changes"
                                            delay={300}
                                            id="saveChanges"
                                        >
                                            <Button
                                                onClick={() => {
                                                    handleUpdateStatusForSave("PREPARING");
                                                }}
                                                circular
                                                size="large"
                                                type="secondary"
                                            >
                                                <Icon tokens={saveTokens} color="primary" />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip
                                            position="top"
                                            title="Recalculate"
                                            id="recalculate"
                                        >
                                            <Button
                                                onClick={syncDataInEdit}
                                                circular
                                                size="large"
                                                type="secondary"
                                            >
                                                <Icon tokens={syncTokens} color="primary" />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip
                                            position="top"
                                            title="Cancel changes"
                                            delay={300}
                                            id="cancelChanges"
                                        >
                                            <Button
                                                onClick={() => {
                                                    handleUpdateStatus("PREPARING");
                                                }}
                                                circular
                                                size="large"
                                                type="secondary"
                                            >
                                                <Icon tokens={closeLargeTokens} color="primary" />
                                            </Button>
                                        </Tooltip>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <Tooltip
                                            position="top"
                                            title="Edit appeal"
                                            delay={300}
                                            id="editAppeal"
                                        >
                                            <Button
                                                onClick={() => {
                                                    handleUpdateStatus("EDITING");
                                                }}
                                                disabled={status !== "PREPARING"}
                                                circular
                                                size="large"
                                                type="secondary"
                                            >
                                                <Icon tokens={editTokens} color="primary" />
                                            </Button>
                                        </Tooltip>
                                    </React.Fragment>
                                )}

                                <Button
                                    onClick={() => {
                                        if (actionButtonBarRef.current) {
                                            actionButtonBarRef.current.scrollIntoView({
                                                behavior: "smooth",
                                                block: "center",
                                            });
                                        }
                                    }}
                                    circular
                                    size="large"
                                    type="secondary"
                                >
                                    <Icon tokens={chevronUpLargeTokens} color="primary" />
                                </Button>
                            </Row>
                        </div>
                    </Box>
                </>
            )}
            {renderErrorAlert()}
        </div>
    );
};
